/**
 * @file ModernPendingChangesControls.tsx
 * @description This file contains the ModernPendingChangesControls component. It
 * is a re-skin of the PendingChangesControls component.
 */

import React from "react";

import { MdDelete, MdSave, MdErrorOutline } from "react-icons/md";
import { StateSetter } from "../../utils/types";

import "./ModernPendingChangesControls.scss";
import { Button, ButtonType, ButtonFrameworkVariant, Spinner } from "../../Components";
import { formatNumber } from "../../utils/format-utils";

interface ModernPendingChangesControlsProps {
  hasPendingChanges: boolean;
  pendingChangesCount: number;
  setShowPendingChanges: StateSetter<boolean>;
  saveChanges: () => void;
  isSaving: boolean;
  clearAllChanges: () => void;
}

export const ModernPendingChangesControls: React.FC<ModernPendingChangesControlsProps> = React.memo(
  ({
    hasPendingChanges,
    pendingChangesCount,
    setShowPendingChanges,
    saveChanges,
    isSaving,
    clearAllChanges,
  }: ModernPendingChangesControlsProps): JSX.Element => {
    return (
      <div className="pendingChangesControls">
        <div className="unsavedChanges">
          {hasPendingChanges && (
            <Button
              type={ButtonType.OUTLINED}
              variant={ButtonFrameworkVariant.LEADING_ICON}
              design="secondary"
              className="danger"
              icon={<MdErrorOutline />}
              onClick={() => {
                setShowPendingChanges(true);
              }}
            >
              See {formatNumber(pendingChangesCount)} Unsaved Change
              {pendingChangesCount > 1 ? "s" : ""}
            </Button>
          )}
        </div>
        <Button
          type={ButtonType.OUTLINED}
          variant={ButtonFrameworkVariant.LEADING_ICON}
          onClick={clearAllChanges}
          disabled={!hasPendingChanges}
          icon={<MdDelete />}
        >
          Discard Changes
        </Button>
        <Button
          type={ButtonType.FILLED}
          variant={ButtonFrameworkVariant.LEADING_ICON}
          onClick={saveChanges}
          disabled={isSaving || !hasPendingChanges}
          icon={<MdSave />}
        >
          {isSaving ? <Spinner /> : "Save"}
        </Button>
      </div>
    );
  }
);
